"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { ArrowRight } from "lucide-react"
import { SUPPORT_EMAIL } from "@/lib/site-config"
import { useTranslations } from "@/components/i18n/use-translations"

const IOC_TYPES = ["ip", "domain", "url", "hash", "email"]

export default function SubmitFeed() {
    const [formData, setFormData] = useState({
        iocType: "",
        feedUrl: "",
        description: ""
    })
    const [charCount, setCharCount] = useState(0)
    const [isValid, setIsValid] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const { messages } = useTranslations();

    const handleIOCTypeChange = (value: string) => {
        setFormData(prev => ({ ...prev, iocType: value }))
        validateForm({ ...formData, iocType: value })
    }

    const handleFeedUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim()
        setFormData(prev => ({ ...prev, feedUrl: value }))
        validateForm({ ...formData, feedUrl: value })
    }

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value
        const truncated = value.slice(0, 500)
        setCharCount(truncated.length)
        setFormData(prev => ({ ...prev, description: truncated }))
        validateForm({ ...formData, description: truncated })
    }

    const validateForm = (data: typeof formData) => {
        const hasIOCType = data.iocType.length > 0
        const hasFeedUrl = /^https?:\/\/.+\..+/.test(data.feedUrl)
        const hasDescription = data.description.trim().length > 0
        setIsValid(hasIOCType && hasFeedUrl && hasDescription)
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        if (!isValid) return

        const subject = `ScaMatrix - New IOC Feed Submission (${formData.iocType.toUpperCase()})`
        const body = `
Feed Type: ${formData.iocType.toUpperCase()}
Feed URL: ${formData.feedUrl}

Description:
${formData.description}

---
Submitted via ScaMatrix Feed Submission Form
        `.trim()

        const mailtoLink = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`

        window.location.href = mailtoLink
        setSubmitted(true)

        setTimeout(() => {
            setFormData({ iocType: "", feedUrl: "", description: "" })
            setCharCount(0)
            setSubmitted(false)
        }, 1500)
    }

    return (
        <div className="mx-auto max-w-2xl px-4 py-8">
            <header className="space-y-3 text-center mb-8">
                <h1 className="text-3xl font-semibold tracking-tight text-white">{messages.submitFeed.title}</h1>
                <p className="text-sm text-gray-400">{messages.submitFeed.description}</p>
            </header>

            <div className="space-y-6 rounded-2xl border border-cyan-700/40 bg-slate-900/50 p-6 shadow-lg backdrop-blur-xs">
                <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-4">
                    <p className="text-sm text-cyan-300">
                        <span className="font-semibold">💡 {messages.submitFeed.tipLabel}:</span> {messages.submitFeed.tipText}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                        <label htmlFor="ioc-type" className="block text-sm font-semibold text-white">
                            {messages.submitFeed.iocTypeLabel} <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-400 mb-2">{messages.submitFeed.iocTypeHelp}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {IOC_TYPES.map(type => (
                                <button
                                    key={type}
                                    type="button"
                                    onClick={() => handleIOCTypeChange(type)}
                                    className={`
                                        relative rounded-lg border-2 px-4 py-3 text-center transition-all duration-200 font-semibold uppercase text-xs tracking-wider
                                        ${formData.iocType === type 
                                            ? "border-cyan-600 bg-linear-to-r from-cyan-600/20 to-blue-600/20 text-cyan-300" 
                                            : "border-cyan-700/40 text-gray-300 hover:border-cyan-600/50 hover:bg-slate-800/50"
                                        }
                                    `}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="feed-url" className="block text-sm font-semibold text-white">
                            {messages.submitFeed.feedUrlLabel} <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-400 mb-2">{messages.submitFeed.feedUrlHelp}</p>
                        <Input
                            id="feed-url"
                            type="url"
                            placeholder={messages.submitFeed.feedUrlPlaceholder}
                            value={formData.feedUrl}
                            onChange={handleFeedUrlChange}
                            className="w-full rounded-lg border border-cyan-700/40 bg-slate-800/50 px-4 py-3 text-white placeholder:text-gray-600 focus:border-cyan-600 focus:outline-hidden focus:ring-2 focus:ring-cyan-600/20"
                            required
                        />
                        <p className="text-xs text-gray-500">✓ {messages.submitFeed.feedFormats}</p>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="description" className="flex justify-between items-center">
                            <span className="block text-sm font-semibold text-white">
                                {messages.submitFeed.descriptionLabel} <span className="text-red-500">*</span>
                            </span>
                            <span className={`text-xs ${charCount >= 500 ? "text-red-500" : "text-gray-500"}`}>
                                {charCount}/500
                            </span>
                        </label>
                        <p className="text-xs text-gray-400">{messages.submitFeed.descriptionHelp}</p>
                        <Textarea
                            id="description"
                            placeholder={messages.submitFeed.descriptionPlaceholder}
                            value={formData.description}
                            onChange={handleDescriptionChange}
                            className="min-h-[140px] border border-cyan-700/40 bg-slate-800/50 text-white placeholder:text-gray-600 focus:border-cyan-600 focus:outline-hidden focus:ring-2 focus:ring-cyan-600/20"
                            required
                        />
                    </div>

                    <Button
                        type="submit"
                        className="w-full rounded-lg bg-linear-to-r from-cyan-500 to-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-cyan-500/30 hover:from-cyan-400 hover:to-blue-500"
                        disabled={!isValid}
                    >
                        <span className="flex items-center justify-center gap-2">
                            {submitted ? messages.submitFeed.submitted : messages.submitFeed.submitButton}
                            <ArrowRight className="h-4 w-4" />
                        </span>
                    </Button>
                </form>
            </div>
        </div>
    )
}
